import React from "react";
import { useTranslation } from "react-i18next";

const baseColor = "#06a7c8";
const accentColor = "#26b36b";
const lightBorder = "#d6d6d6";

const ClinicPrintFrame = ({
  doctorName,
  doctorTitle,
  date,
  patientName,
  patientAge,
  patientGender,
  leftNotes,
  diagnosis,
  rxTitle = "Rx",
  children,
  footerText,
  compact = false,
}) => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        width: "210mm",
        height: "297mm",
        margin: "0 auto",
        backgroundColor: "#fff",
        border: `1px solid ${lightBorder}`,
        boxSizing: "border-box",
        padding: compact ? "8mm 9mm" : "9mm 10mm",
        fontFamily: "Arial, sans-serif",
        color: "#111",
        position: "relative",
        overflow: "hidden",
        pageBreakInside: "avoid",
        breakInside: "avoid-page",
      }}
    >
      <div style={{ marginBottom: "6mm" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div
            style={{
              width: "45%",
              height: "4mm",
              background: baseColor,
              clipPath: "polygon(0 0, 92% 0, 82% 100%, 0 100%)",
            }}
          />
          <div
            style={{
              width: "45%",
              height: "4mm",
              background: baseColor,
              clipPath: "polygon(8% 0, 100% 0, 100% 100%, 18% 100%)",
            }}
          />
        </div>
      </div>

      <header
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          gap: "8px",
          alignItems: "center",
          marginBottom: "4mm",
        }}
      >
        <div>
          <div style={{ fontSize: "7mm", color: baseColor, fontWeight: 700 }}>
            {t("print.turkishClinic")}
          </div>
          {doctorName && (
            <div style={{ fontSize: "4mm", fontWeight: 700, color: "#1f1f1f", marginTop: "1mm" }}>
              {doctorName}
            </div>
          )}
          {doctorTitle ? (
            <div style={{ fontSize: "3.2mm", lineHeight: 1.2, fontWeight: 700 }}>{doctorTitle}</div>
          ) : null}
        </div>

        <img
           src="/daudzai-logo.jpeg"
              alt="Company Logo"
          style={{ width: "24mm", height: "24mm", objectFit: "contain" }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />

        <div style={{ textAlign: "right", direction: "rtl" }}>
          <div style={{ fontSize: "7mm", color: baseColor, fontWeight: 700 }}>
            KIMS SHIFA HOSPITAL
          </div>
          <div style={{ fontSize: "3.2mm", lineHeight: 1.2, fontWeight: 700 }}>
            {t("print.tagline")}
          </div>
        </div>
      </header>

      <div
        style={{
          height: "1.6mm",
          width: "66%",
          margin: "0 auto 4mm",
          background: accentColor,
          borderRadius: "999px",
        }}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.3fr 1.1fr 0.7fr 0.7fr 0.9fr",
          alignItems: "center",
          gap: "4px",
          fontSize: "3.8mm",
          fontWeight: "bold",
          color: "#0b8eb0",
          paddingBottom: "3mm",
          marginBottom: "2mm",
          borderBottom: `1px solid ${lightBorder}`,
        }}
      >
        <div>Name: {patientName || ".........."}</div>
        <div>Age: {patientAge || "......"}</div>
        <div>Gender: {patientGender || "......"}</div>
        <div style={{ textAlign: "center" }}>
          {doctorName || ""}
        </div>
        <div style={{ textAlign: "right" }}>Date: {date || ".........."}</div>
      </div>

      <div style={{ display: "flex", minHeight: compact ? "170mm" : "190mm" }}>
        <div
          style={{
            width: "25%",
            paddingRight: "3mm",
            borderRight: `1px solid ${baseColor}`,
          }}
        >
          <div
            style={{
              fontSize: "5.5mm",
              color: "#1f1f1f",
              fontStyle: "italic",
              fontWeight: "bold",
              marginBottom: "6mm",
            }}
          >
            Clinical Notes
          </div>

          {(leftNotes || ["B.P", "P.R", "R.R", "T"]).map((item, i) => {
            const label = typeof item === "object" ? item.label : item;
            const value = typeof item === "object" ? item.value : "";
            return (
              <div
                key={i}
                style={{
                  fontWeight: "bold",
                  color: baseColor,
                  marginBottom: "8mm",
                  fontSize: "4.2mm",
                }}
              >
                {label}{value ? `: ${value}` : ""}
              </div>
            );
          })}
          {diagnosis && (
            <div style={{ marginTop: "4mm" }}>
              <div style={{ fontWeight: "bold", color: "#c0392b", fontSize: "4.5mm", marginBottom: "2mm" }}>
                Diagnosis:
              </div>
              <div style={{ fontSize: "4mm", lineHeight: 1.5, fontWeight: "500" }}>{diagnosis}</div>
            </div>
          )}
        </div>

        <div style={{ flex: 1, paddingLeft: "4mm" }}>
          <div
            style={{
              fontSize: "11mm",
              lineHeight: 1,
              color: accentColor,
              fontFamily: "cursive",
              marginBottom: "3mm",
            }}
          >
            {rxTitle}
          </div>
          <div>{children}</div>
        </div>
      </div>

      <footer
        style={{
          position: "absolute",
          left: "10mm",
          right: "10mm",
          bottom: compact ? "8mm" : "10mm",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "7mm",
            background: baseColor,
            clipPath: "polygon(9% 0, 91% 0, 100% 100%, 0 100%)",
          }}
        >
        </div>
        <div
          style={{
            textAlign: "center",
            fontSize: "3.3mm",
            fontWeight: 700,
            marginTop: "2mm",
          }}
        >
          {footerText || t("print.tagline")}
        </div>
      </footer>
    </div>
  );
};

export default ClinicPrintFrame;
